import React from 'react';
import { useTranslation } from 'localization';
import SegmentedControlAtom from '../../components/atoms/SegmentedControl/SegmentedControlAtom';
import StatusBadgeAtom from '../../components/atoms/status-badge/StatusBadgeAtom';
import type { StatusValue } from 'types/spherePoints';
import type { Adapter } from '../types';
import './sphereDomainAdapters.scss';

/**
 * Sphere-domain a2ui primitives (`SphereTypeSelector`, `SphereCard`, `UrgencySelector`,
 * `PerspectiveDisplay`, `SphereAvatar`, `SphereEditor`). These render from already-resolved
 * data-model values only — no direct sphere fetching — matching the declarative-only security
 * invariant (documents/adapters never reach out on their own; the host's `dataModel` is the sole
 * data source, populated by `updateDataModel` messages).
 *
 * The two selector adapters compose `SegmentedControlAtom` rather than hand-rolling a button
 * strip; the status chip composes `StatusBadgeAtom`. `SphereAvatarAdapter` is the one bespoke
 * visual here: the existing `sphere-avatar` atom renders a GLYPH, and this adapter renders an
 * uploaded image with an initials fallback — a different primitive, not a variant of that one.
 */

const STATUS_VALUES: readonly StatusValue[] = [
  'on_track',
  'at_risk',
  'needs_attention',
  'critical',
  'blocked',
  'unknown',
];

function asStatusValue(value: unknown): StatusValue | undefined {
  return typeof value === 'string' && (STATUS_VALUES as readonly string[]).includes(value)
    ? (value as StatusValue)
    : undefined;
}

const URGENCY_LEVELS = ['1', '2', '3', '4', '5'];

export const SphereTypeSelectorAdapter: Adapter = ({ componentId, props, ctx }) => {
  const value = props.value !== undefined ? (ctx.resolveValue(props.value) as string) : undefined;
  const schemas = (ctx.resolveValue(props.schemas) as Array<{ systemName: string; name: string }>) ?? [];
  const onValueChanged = props.onValueChanged as string | undefined;

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__type-selector">
      <SegmentedControlAtom
        options={schemas.map((schema) => ({
          value: schema.systemName,
          label: schema.name,
          disabled: Boolean(props.disabled),
        }))}
        value={value ?? ''}
        mode="single"
        variant="pills"
        onChange={(next) => {
          if (onValueChanged) {
            ctx.onAction({ name: onValueChanged, componentId, context: { value: next as string } });
          }
        }}
      />
    </div>
  );
};

export const SphereCardAdapter: Adapter = ({ componentId, props, ctx }) => {
  const sphere = ctx.resolveValue(props.sphere) as { name?: string; schemaSystemName?: string; status?: string } | undefined;
  const onAction = props.onAction as string | undefined;
  const status = asStatusValue(sphere?.status);

  const body = (
    <>
      <span className="sphere-domain-adapter__sphere-card-name">{sphere?.name}</span>
      {status ? (
        <StatusBadgeAtom status={status} variant="chip" showLabel className="sphere-domain-adapter__sphere-card-status" />
      ) : (
        sphere?.status && <span className="sphere-domain-adapter__sphere-card-status">{sphere.status}</span>
      )}
    </>
  );

  // A real `<button>` rather than `<div role="button">`, so the card is reachable and
  // activatable by keyboard without hand-rolling key handling (Sonar S1082 / S6819).
  if (onAction) {
    return (
      <button
        type="button"
        data-component-id={componentId}
        className="sphere-domain-adapter__sphere-card sphere-domain-adapter__sphere-card--interactive"
        onClick={() => {
          ctx.onAction({ name: onAction, componentId });
        }}
      >
        {body}
      </button>
    );
  }

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__sphere-card">
      {body}
    </div>
  );
};

export const UrgencySelectorAdapter: Adapter = ({ componentId, props, ctx }) => {
  const value = props.value !== undefined ? Number(ctx.resolveValue(props.value)) : 3;
  const onValueChanged = props.onValueChanged as string | undefined;
  const disabled = Boolean(props.disabled);

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__urgency-selector">
      <SegmentedControlAtom
        options={URGENCY_LEVELS.map((level) => ({ value: level, label: level, disabled }))}
        value={String(value)}
        mode="single"
        onChange={(next) => {
          if (onValueChanged) {
            ctx.onAction({ name: onValueChanged, componentId, context: { value: Number(next) } });
          }
        }}
      />
    </div>
  );
};

export const PerspectiveDisplayAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const assigneeName = props.assigneeName !== undefined ? (ctx.resolveValue(props.assigneeName) as string) : undefined;

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__perspective-display">
      {label && <span className="sphere-domain-adapter__perspective-label">{label}</span>}
      <span>{assigneeName ?? t('space.render.unassigned')}</span>
      {Boolean(props.required) && <span className="sphere-domain-adapter__perspective-required">{t('space.render.required')}</span>}
    </div>
  );
};

export const SphereAvatarAdapter: Adapter = ({ componentId, props, ctx }) => {
  const sphereName = props.sphereName !== undefined ? (ctx.resolveValue(props.sphereName) as string) : undefined;
  const avatar = props.avatar !== undefined ? (ctx.resolveValue(props.avatar) as string) : undefined;
  const initials = sphereName ? sphereName.slice(0, 2).toUpperCase() : '?';

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__sphere-avatar" aria-label={sphereName}>
      {avatar ? <img src={avatar} alt={sphereName ?? ''} /> : <span>{initials}</span>}
    </div>
  );
};

export const SphereEditorAdapter: Adapter = ({ componentId, props, ctx }) => {
  const title = props.title !== undefined ? (ctx.resolveValue(props.title) as string) : undefined;
  const sphere = ctx.resolveValue(props.sphere) as Record<string, unknown> | undefined;
  const schema = ctx.resolveValue(props.schema) as { properties?: Array<{ systemName: string; displayName?: string }> } | undefined;
  // No input control lives in this adapter (display-only, no onChange) — `editable` only
  // toggles `aria-readonly`, so it is driven purely by the document's own prop, not by mode.
  const editable = props.editable !== false;

  return (
    <div data-component-id={componentId} className="sphere-domain-adapter__sphere-editor">
      {title && <h4>{title}</h4>}
      {(schema?.properties ?? []).map((property) => (
        <div key={property.systemName} className="sphere-domain-adapter__sphere-editor-field">
          <label>{property.displayName ?? property.systemName}</label>
          <span aria-readonly={!editable}>{String((sphere?.properties as Record<string, unknown> | undefined)?.[property.systemName] ?? '')}</span>
        </div>
      ))}
    </div>
  );
};
